/**
 * Central Discovery Queue Manager
 * 
 * Manages the centralized queue of discovery items that need to be confirmed.
 * Strategies push items into this queue, and the ChatInterface picks them up
 * one at a time for user confirmation.
 */

import { runAIMatches, runMultiMatches, fetchQueueItemConfig } from './api';
import { 
  DISCOVERY_STATUS, 
  DISCOVERY_TEMPLATE_TYPES,
  validateQueueItem,
  createSkillsQueueItem,
  createLocationQueueItem,
  createCompanyStageQueueItem,
  createExperienceQueueItem
} from './discoveryQueueSchema';

/**
 * Enhanced Discovery Queue Item Structure:
 * Uses the comprehensive schema from discoveryQueueSchema.js
 * 
 * {
 *   topic: string,
 *   icp_requirement: {
 *     section_id: string,
 *     section_name: string,
 *     bool_str: string,
 *     section_type: 'Must Have' | 'Nice to Have'
 *   },
 *   status: 'discovery_pending' | 'discovery_in_progress' | 'discovery_done' | 'discovery_skipped',
 *   discovery_config: {
 *     template_type: string,
 *     ai_intro_message: string,
 *     ai_confirmation_message: string,
 *     ux_config: {
 *       title: string,
 *       subtitle?: string,
 *       chart_title?: string,
 *       chart_subtitle?: string,
 *       confirm_button_text: string
 *     },
 *     template_props: object
 *   },
 *   discovery_result: object | null,
 *   created_at: number,
 *   updated_at: number
 * }
 */

/**
 * Central Discovery Queue Class
 * Manages the discovery queue state and provides methods to interact with it
 */
class CentralDiscoveryQueue {
  constructor() {
    this.queue = new Map(); // Use Map for efficient lookups by queue_id
    this.sessionId = null;
    this.listeners = new Set();
    this.isLoading = false;
    this.lastSaved = null;
    this.autoSaveTimer = null;
    this.queueItemConfig = null; // Will be loaded from API
    this.roleTypeConfig = null; // Will be loaded from API
    this.seedingLocks = {}; // per-source seeding locks to prevent duplicate adds
  }

  /**
   * Get queue item configuration based on section ID
   * @param {string} sectionId - The section ID (e.g., 'company_stage', 'fmmagic')
   * @returns {Object|null} Configuration object or null if not found
   */
  getQueueItemConfig(sectionId) {
    if (!this.queueItemConfig || !sectionId) {
      return null;
    }

    // Get configuration for the specific section
    return this.queueItemConfig[sectionId] || null;
  }

  /**
   * Initialize the queue for a session
   * @param {string} sessionId - The session ID
   * @param {Array} initialItems - Initial queue items (optional)
   * @param {Object} roleTypeConfig - Role type configuration (optional)
   */
  async initialize(sessionId, initialItems = [], roleTypeConfig = null, options = {}) {
    const prevSessionId = this.sessionId;
    this.sessionId = sessionId;
    this.roleTypeConfig = roleTypeConfig;
    this.isLoading = true;

    // Only clear local queue when switching sessions or when explicitly forced.
    const { forceReset = false } = options || {};
    if (forceReset || prevSessionId !== sessionId) {
      try {
        this.queue.clear();
        console.log('🧼 CENTRAL QUEUE: Cleared local queue at initialize start for session:', sessionId);
        // Notify listeners so any read-only UIs drop immediately even if backend has nothing yet
        this.notifyListeners();
      } catch (_) {}
    } else {
      console.log('🧼 CENTRAL QUEUE: Skipping local clear on initialize (same session, no forceReset)');
    }
    
    console.log('🔄 CENTRAL QUEUE: Initializing for session:', sessionId, 'with initialItems:', initialItems.length, 'roleType:', roleTypeConfig?.id);
    
    try {
      // Load queue item configuration from API
      if (!this.queueItemConfig) {
        console.log('🔄 CENTRAL QUEUE: Loading queue item configuration...');
        this.queueItemConfig = await fetchQueueItemConfig();
        console.log('✅ CENTRAL QUEUE: Loaded queue item config:', Object.keys(this.queueItemConfig).length, 'items');
      }
      
      // Try to load existing queue from database
      console.log('🔄 CENTRAL QUEUE: Attempting to load from database...');
      const savedData = await this.loadFromDatabase();
      
      console.log('🔄 CENTRAL QUEUE: Database load result:', {
        savedData: savedData ? savedData.length : 'null',
        savedDataDetails: savedData ? savedData.map(item => ({
          section_id: item.section_id,
          topic: item.topic,
          disco_done: item.disco_done
        })) : 'null'
      });
      
      if (savedData && savedData.length > 0) {
        // Restore queue from saved data using queue_id as key
        this.queue.clear();
        savedData.forEach(item => {
          const key = item.queue_id || item.topic; // Fallback to topic for backward compatibility
          this.queue.set(key, item);
        });
        console.log('✅ CENTRAL QUEUE: Loaded discovery queue from database:', this.queue.size, 'items');
      } else if (initialItems && initialItems.length > 0) {
        // Initialize queue from initial items using queue_id as key
        this.queue.clear();
        initialItems.forEach(item => {
          if (validateQueueItem(item).isValid) {
            const key = item.queue_id || item.topic; // Fallback to topic for backward compatibility
            this.queue.set(key, item);
          }
        });
        console.log('✅ CENTRAL QUEUE: Initialized discovery queue from items:', this.queue.size, 'items');
        
        // Save initial state
        await this.saveToDatabase();
      } else {
        console.log('⚠️ CENTRAL QUEUE: No saved data and no initial items - queue remains empty');
      }
      
      this.isLoading = false;
      this.notifyListeners();
      
    } catch (error) {
      console.error('❌ CENTRAL QUEUE: Error initializing discovery queue:', error);
      this.isLoading = false;
      
      // Fall back to initializing from items if available
      if (initialItems && initialItems.length > 0) {
        this.queue.clear();
        initialItems.forEach(item => {
          if (validateQueueItem(item).isValid) {
            this.queue.set(item.topic, item);
          }
        });
        this.notifyListeners();
      }
    }
  }

  /**
   * Add or update a discovery queue item
   * @param {Object} queueItem - Queue item with flat structure
   * @returns {Object} The added/updated item
   */
  async addQueueItem(queueItem) {
    // Basic validation for flat structure
    if (!queueItem.topic || !queueItem.section_name || !queueItem.queue_id) {
      console.error('Invalid queue item: missing topic, section_name, or queue_id', queueItem);
      throw new Error('Invalid queue item: missing topic, section_name, or queue_id');
    }

    // Use queue_id as the key to allow duplicates of the same topic
    const queueId = queueItem.queue_id;
    
    // Check if item with this exact queue_id already exists
    const existingItem = this.queue.get(queueId);
    if (existingItem && !existingItem.disco_done) {
      // Update existing item (same queue_id) - REPLACE bool_str, don't merge
      const updatedItem = {
        ...existingItem,
        ...queueItem,
        // IMPORTANT: Replace bool_str completely, don't merge
        bool_str: queueItem.bool_str || existingItem.bool_str,
        updated_at: Date.now()
      };
      
      this.queue.set(queueId, updatedItem);
      console.log('🎯 CENTRAL QUEUE: Updated item for queue_id:', queueId, 'bool_str replaced with:', queueItem.bool_str);
    } else {
      // Add new item (new queue_id)
      const newItem = {
        ...queueItem,
        created_at: queueItem.created_at || Date.now(),
        updated_at: Date.now()
      };
      
      // Apply queue item configuration from JSON using flat structure
      if (this.queueItemConfig && queueItem.section_id) {
        const config = this.getQueueItemConfig(queueItem.section_id);
        if (config) {
          // Apply section_type from configuration
          if (config.section_type) {
            newItem.section_type = config.section_type;
          }
          
          // Apply default_skipped from configuration
          if (config.default_skipped === true) {
            newItem.disco_done = false; // Keep as not done so it still appears
            newItem.skipped = true; // Mark as skipped by default
            console.log(`🔧 CENTRAL QUEUE: Auto-skipped ${queueItem.section_id} based on configuration`);
          }
        } else {
          console.log(`🔧 CENTRAL QUEUE: No configuration found for section: ${queueItem.section_id}`);
        }
      }

      // Override: when forceDiscovered is set (e.g., JD seed), ensure completed and not skipped
      if (queueItem._forceDiscovered === true) {
        newItem.disco_done = true;
        newItem.status = DISCOVERY_STATUS.DONE;
        newItem.skipped = false;
        newItem._seededFrom = queueItem._seededFrom || 'jd';
      }
      
      this.queue.set(queueId, newItem);
      console.log('🎯 CENTRAL QUEUE: Added new item for queue_id:', queueId, 'topic:', queueItem.topic);
    }

    this.notifyListeners();
    this.scheduleAutoSave();
    return this.queue.get(queueId);
  }


  /**
   * Add ICP requirement using the new schema format
   * @param {string} topic - Topic name
   * @param {Object} icpRequirement - ICP requirement object
   * @param {string} status - Discovery status
   * @param {Object} discoveryConfig - Discovery configuration
   */
  async addICPRequirement(topic, icpRequirement, status = DISCOVERY_STATUS.PENDING, discoveryConfig = null) {
    // Create a complete queue item
    const queueItem = {
      topic,
      icp_requirement: icpRequirement,
      status,
      discovery_config: discoveryConfig || this.getDefaultDiscoveryConfig(topic),
      discovery_result: null,
      created_at: Date.now(),
      updated_at: Date.now()
    };

    return await this.addQueueItem(queueItem);
  }

  /**
   * Get default discovery configuration for a topic
   * @param {string} topic - Topic name
   * @returns {Object} Default discovery configuration
   */
  getDefaultDiscoveryConfig(topic) {
    const defaultConfigs = {
      skills: {
        template_type: DISCOVERY_TEMPLATE_TYPES.SINGLE_ROW_SELECTOR,
        ai_intro_message: "Based on your role description, I've identified some key technical skills. Let me confirm these with you.",
        ai_confirmation_message: "Perfect! I've noted your required skills. This will help us find candidates with the right technical background.",
        ux_config: {
          title: "Got it! Just to confirm — you're looking for candidates with the following skills, right? 👇",
          confirm_button_text: "Confirm Skills"
        },
        template_props: {
          items: [],
          selectedItems: []
        }
      },
      location: {
        template_type: DISCOVERY_TEMPLATE_TYPES.MULTI_ROW_SELECTOR,
        ai_intro_message: "Now let's talk about location preferences. I've analyzed where similar candidates are typically found.",
        ai_confirmation_message: "Great choice! I've updated your location preferences. This will help us target the right talent pools.",
        ux_config: {
          title: "Based on what you're looking for, here are the top locations where similar candidates are most commonly found 🌍\nPick the location(s) you'd like to focus on, or feel free to make the role remote! 🗺️✨",
          confirm_button_text: "Confirm Location"
        },
        template_props: {
          sections: [],
          selectedItems: []
        }
      },
      company_stage: {
        template_type: DISCOVERY_TEMPLATE_TYPES.DISTRIBUTION_BAR_CHART,
        ai_intro_message: "Let me show you the company stage distribution of your current workforce to help guide your preferences.",
        ai_confirmation_message: "Excellent! I've noted your company stage preferences. This will help us find candidates with relevant experience.",
        ux_config: {
          title: "I reviewed your existing workforce, and this is the distribution across different company stages. Which company stage(s) would you like to target?",
          chart_title: "Recent Company Stages",
          chart_subtitle: "Employees who recently worked at these company stages",
          confirm_button_text: "Select at Least One Option"
        },
        template_props: {
          data: [],
          selectedItems: []
        }
      },
      experience: {
        template_type: DISCOVERY_TEMPLATE_TYPES.INLINE_SLIDER,
        ai_intro_message: "Now let's determine the ideal experience level. I'll show you the current distribution to help guide your decision.",
        ai_confirmation_message: "Perfect! I've set your experience range preferences. This gives us a good target for candidate experience levels.",
        ux_config: {
          title: "Let's take a look at the ideal work experience for this role. Based on your search criteria, here's how your current employee base is distributed by years of experience. Go ahead and select a range below to refine your candidate search. 👇",
          chart_title: "Work Experience",
          confirm_button_text: "Confirm Work Experience"
        },
        template_props: {
          value: [2, 17],
          min: 0,
          max: 30,
          candidateCount: 12000,
          showSparkline: true
        }
      },
      title: {
        template_type: DISCOVERY_TEMPLATE_TYPES.MULTI_ROW_SELECTOR,
        ai_intro_message: "Let's confirm the job titles you want to target.",
        ai_confirmation_message: "Titles noted. We'll use these to guide targeting.",
        ux_config: {
          title: "Select the job titles that fit this role",
          confirm_button_text: "Confirm Titles",
          allow_empty_selection: true
        },
        template_props: {
          sections: [],
          selectedItems: []
        }
      }
    };

    return defaultConfigs[topic] || defaultConfigs.skills;
  }

  /**
   * Mark an item as discovered/completed
   * @param {string} queueId - The queue ID
   * @param {Object} discoveryResult - The discovery result
   */
  async markAsDiscovered(queueId, discoveryResult) {
    const item = this.queue.get(queueId);
    if (!item) {
      console.warn('Cannot mark unknown queue_id as discovered:', queueId);
      return;
    }

    const updatedItem = {
      ...item,
      ...discoveryResult,
      updated_at: Date.now()
    };

    // Handle different item states based on discoveryResult
    if (discoveryResult && discoveryResult.skipped === true) {
      // Skipping an item
      updatedItem.disco_done = false; 
      updatedItem.status = DISCOVERY_STATUS.PENDING; 
      console.log('🚫 CENTRAL QUEUE: Marked as skipped:', queueId, 'topic:', item.topic);
    } else if (discoveryResult && discoveryResult.hasOwnProperty('disco_done')) {
      // Explicitly setting disco_done (for unskip or restart scenarios)
      updatedItem.disco_done = discoveryResult.disco_done;
      updatedItem.status = discoveryResult.disco_done ? DISCOVERY_STATUS.DONE : DISCOVERY_STATUS.PENDING;
      if (discoveryResult.disco_done) {
        updatedItem.discovery_result = discoveryResult;
        console.log('✅ CENTRAL QUEUE: Marked as discovered:', queueId, 'topic:', item.topic);
      } else {
        console.log('🔄 CENTRAL QUEUE: Reset to pending:', queueId, 'topic:', item.topic);
      }
    } else {
      // Default behavior - mark as completed (for normal discovery completion)
      updatedItem.disco_done = true;
      updatedItem.status = DISCOVERY_STATUS.DONE;
      updatedItem.discovery_result = discoveryResult;
      console.log('✅ CENTRAL QUEUE: Marked as discovered:', queueId, 'topic:', item.topic);
    }

    // Update bool_str if provided in discoveryResult
    if (discoveryResult && discoveryResult.bool_str !== undefined) {
      updatedItem.bool_str = discoveryResult.bool_str;
      console.log('🎯 CENTRAL QUEUE: Updated bool_str for', queueId, 'from:', item.bool_str, 'to:', discoveryResult.bool_str);
    }

    this.queue.set(queueId, updatedItem);

    this.notifyListeners();
    await this.saveToDatabase(); // Immediate save for completed/skipped items
  }

  /**
   * Update section type for a queue item
   * @param {string} sectionId - The section ID
   * @param {string} sectionType - The new section type ('Must Have' or 'Nice to Have')
   */
  async updateSectionType(sectionId, sectionType) {
    // Update ALL items that share this section_id (some sections like Tool Experience reuse the same id across multiple queue items)
    const items = this.getAllItems();
    const matches = items.filter(it => it.section_id === sectionId);

    if (!matches || matches.length === 0) {
      console.warn('Cannot update section type for unknown section_id:', sectionId);
      return;
    }

    let updatedCount = 0;
    const updatedKeys = [];

    for (const item of matches) {
      const queueId = item.queue_id || item.topic; // Fallback to topic for backward compatibility
      const updatedItem = {
        ...item,
        section_type: sectionType,
        updated_at: Date.now()
      };
      this.queue.set(queueId, updatedItem);
      updatedKeys.push(queueId);
      updatedCount++;
    }

    console.log('🎯 CENTRAL QUEUE: Updated section_type for', sectionId, 'to:', sectionType, 'across', updatedCount, 'item(s)');

    this.notifyListeners();
    await this.saveToDatabase(); // Immediate save for section type changes

    // Return array of updated queueIds for potential UI reconciliation
    return updatedKeys;
  }

  /**
   * Get all queue items
   * @returns {Array} Array of all queue items
   */
  getAllItems() {
    return Array.from(this.queue.values());
  }

  /**
   * Get items by status (using disco_done for flat structure)
   * @param {string} status - Status to filter by
   * @returns {Array} Array of items with the specified status
   */
  getItemsByStatus(status) {
    if (status === DISCOVERY_STATUS.DONE) {
      return this.getAllItems().filter(item => item.disco_done === true);
    } else if (status === DISCOVERY_STATUS.PENDING) {
      return this.getAllItems().filter(item => item.disco_done === false || item.disco_done === undefined);
    }
    return this.getAllItems().filter(item => item.status === status);
  }

  /**
   * Get pending items
   * @returns {Array} Array of pending queue items
   */
  getPendingItems() {
    return this.getAllItems().filter(item => 
      (item.disco_done === false || item.disco_done === undefined) && 
      (item.skipped === false || item.skipped === undefined)
    );
  }

  /**
   * Get completed items
   * @returns {Array} Array of completed queue items
   */
  getCompletedItems() {
    return this.getAllItems().filter(item => item.disco_done === true);
  }

  /**
   * Get next pending item
   * @returns {Object|null} Next pending item or null
   */
  getNextPendingItem() {
    const pendingItems = this.getPendingItems();
    return pendingItems.length > 0 ? pendingItems[0] : null;
  }

  /**
   * Get items by topic
   * @param {string} topic - Topic to filter by
   * @returns {Array} Array of items with the specified topic, sorted by section_order
   */
  getItemsByTopic(topic) {
    const sorted = this.getAllItems()
      .filter(item => item.topic === topic)
      .sort((a, b) => {
        // Sort by section_order (ascending), then by created_at as fallback
        const orderA = a.section_order || 999;
        const orderB = b.section_order || 999;
        if (orderA !== orderB) {
          return orderA - orderB;
        }
        // If section_order is the same, sort by creation time
        return (a.created_at || 0) - (b.created_at || 0);
      });
    // Deduplicate by section_id to avoid multiple blocks for the same section
    const seen = new Set();
    const deduped = [];
    for (const it of sorted) {
      const key = it.section_id || it.queue_id;
      if (key && !seen.has(key)) {
        seen.add(key);
        deduped.push(it);
      }
    }
    return deduped;
  }

  /**
   * Get item by topic
   * @param {string} topic - Topic to get
   * @returns {Object|null} Queue item or null
   */
  getItemByTopic(topic) {
    return this.queue.get(topic) || null;
  }

  /**
   * Check if all items are discovered
   * @returns {boolean} True if all items are discovered
   */
  isFullyDiscovered() {
    const allItems = this.getAllItems();
    return allItems.length > 0 && allItems.every(item => item.status === DISCOVERY_STATUS.DONE);
  }

  /**
   * Get discovery progress percentage
   * @returns {number} Progress percentage (0-100)
   */
  getProgressPercentage() {
    const allItems = this.getAllItems();
    if (allItems.length === 0) return 0;
    
    const completedItems = this.getCompletedItems();
    return Math.round((completedItems.length / allItems.length) * 100);
  }

  /**
   * Get current ICP requirements (completed items)
   * @returns {Array} Array of ICP requirements
   */
  getCurrentICPRequirements() {
    return this.getCompletedItems()
      .filter(item => item.bool_str)
      .map(item => ({
        section_id: item.section_id,
        section_name: item.section_name,
        bool_str: item.bool_str,
        section_type: item.section_type
      }));
  }

  /**
   * Merge boolean strings with OR operator
   * @param {string} existing - Existing boolean string
   * @param {string} newBoolStr - New boolean string to merge
   * @returns {string} Merged boolean string
   */
  mergeBoolStrings(existing, newBoolStr) {
    if (!existing) return newBoolStr || '';
    if (!newBoolStr) return existing;
    
    // If they're the same, return one
    if (existing === newBoolStr) return existing;
    
    // Combine with OR
    return `(${existing}) OR (${newBoolStr})`;
  }

  /**
   * Add a listener for queue changes
   * @param {Function} listener - Callback function
   */
  addListener(listener) {
    this.listeners.add(listener);
  }

  /**
   * Remove a listener
   * @param {Function} listener - Callback function to remove
   */
  removeListener(listener) {
    this.listeners.delete(listener);
  }

  /**
   * Notify all listeners of queue changes
   */
  notifyListeners() {
    this.listeners.forEach(listener => {
      try {
        listener(this.getAllItems());
      } catch (error) {
        console.error('Error in queue listener:', error);
      }
    });
  }

  /**
   * Schedule auto-save to database
   */
  scheduleAutoSave() {
    if (this.autoSaveTimer) {
      clearTimeout(this.autoSaveTimer);
    }

    this.autoSaveTimer = setTimeout(async () => {
      try {
        await this.saveToDatabase();
      } catch (error) {
        console.error('Auto-save failed:', error);
      }
    }, 2000); // Auto-save after 2 seconds of inactivity
  }

  /**
   * Save queue to database using backend API
   */
  async saveToDatabase() {
    if (!this.sessionId) {
      console.warn('🚫 CENTRAL QUEUE: Cannot save queue: no session ID');
      return;
    }

    console.log('💾 CENTRAL QUEUE: Saving discovery queue for session:', this.sessionId);

    try {
      // Import the API function dynamically to avoid circular dependencies
      const { updateDiscoveryProgress } = await import('./api');
      
      const items = this.getAllItems();
      const result = await updateDiscoveryProgress(this.sessionId, items);
      
      if (result) {
        this.lastSaved = new Date().toISOString();
        console.log('✅ CENTRAL QUEUE: Discovery queue saved successfully to backend');
        
        // 🎯 CRITICAL FIX: Notify listeners after successful save
        // This ensures ChatInterface re-renders with the updated queue state
        console.log('🔄 CENTRAL QUEUE: Notifying listeners after successful save');
        this.notifyListeners();
      } else {
        throw new Error('Failed to save discovery queue to backend');
      }
      
    } catch (error) {
      console.error('❌ CENTRAL QUEUE: Error saving discovery queue:', error);
      // Disabled localStorage fallback per policy
      console.warn('⚠️ CENTRAL QUEUE: Skipping localStorage persistence of queue (no client-side caching).');
    }
  }

  /**
   * Load queue from database using backend API
   */
  async loadFromDatabase() {
    if (!this.sessionId) {
      console.warn('Cannot load queue: no session ID');
      return null;
    }

    try {
      // Import the API function dynamically to avoid circular dependencies
      const { getDiscoveryProgress } = await import('./api');
      
      console.log('📥 CENTRAL QUEUE: Loading discovery queue from backend for session:', this.sessionId);
      const result = await getDiscoveryProgress(this.sessionId);
      
      if (result && result.items && Array.isArray(result.items)) {
        console.log('✅ CENTRAL QUEUE: Loaded discovery queue from backend:', result.items.length, 'items');
        console.log('✅ CENTRAL QUEUE: Items details:', result.items.map(item => ({
          section_id: item.section_id,
          topic: item.topic,
          disco_done: item.disco_done,
          bool_str: item.bool_str
        })));
        return result.items;
      }
      
      console.log('📭 CENTRAL QUEUE: No discovery queue found in backend (expected for new sessions)');
      return null;
      
    } catch (error) {
      console.error('❌ CENTRAL QUEUE: Error loading discovery queue from backend:', error);
      console.warn('⚠️ CENTRAL QUEUE: Skipping localStorage fallback (no client-side caching).');
      return null;
    }
  }

  /**
   * Remove a specific queue item by ID
   * @param {string} queueId - The queue ID to remove
   */
  async removeQueueItem(queueId) {
    if (!this.sessionId) {
      console.warn('Cannot remove item: no session ID');
      return false;
    }

    const item = this.queue.get(queueId);
    if (!item) {
      console.warn('Cannot remove unknown queue_id:', queueId);
      return false;
    }

    try {
      // Import the API function dynamically to avoid circular dependencies
      const { removeQueueItem: apiRemoveQueueItem } = await import('./api');
      
      // Call dedicated remove API
      const result = await apiRemoveQueueItem(this.sessionId, queueId);
      
      if (result) {
        // Remove from local queue only if backend removal succeeded
        this.queue.delete(queueId);
        console.log('🗑️ CENTRAL QUEUE: Removed item from backend and local queue:', queueId, 'topic:', item.topic);
        
        // Force immediate notification to trigger re-renders
        this.notifyListeners();
        
        return true;
      } else {
        console.error('🚨 CENTRAL QUEUE: Backend removal failed for item:', queueId);
        return false;
      }
    } catch (error) {
      console.error('Error removing queue item:', error);
      return false;
    }
  }

  /**
   * Clear the queue
   */
  clear() {
    this.queue.clear();
    this.notifyListeners();
  }

  /**
   * Clear incomplete items for a specific topic (keeps completed items)
   * @param {string} topic - The topic to clear incomplete items for
   */
  async clearIncompleteItemsByTopic(topic) {
    if (!this.sessionId) {
      console.warn('Cannot clear items: no session ID');
      return 0;
    }

    try {
      // Import the API function dynamically to avoid circular dependencies
      const { clearIncompleteItemsByTopic: apiClearIncompleteItems } = await import('./api');
      
      // Call the dedicated backend API to clear incomplete items
      const result = await apiClearIncompleteItems(this.sessionId, topic);
      
      if (result) {
        // Clear items from local queue as well
        const itemsToRemove = [];
        for (const [key, item] of this.queue) {
          if (item.topic === topic && !item.disco_done) {
            itemsToRemove.push(key);
          }
        }
        
        itemsToRemove.forEach(key => {
          this.queue.delete(key);
        });
        
        // Force immediate notification to trigger re-renders
        this.notifyListeners();
        
        return result.removedCount || itemsToRemove.length;
      } else {
        throw new Error('Failed to clear items from backend');
      }
    } catch (error) {
      console.error('Error clearing incomplete items via API:', error);
      
      // Fallback to local clearing if API fails
      const itemsToRemove = [];
      for (const [key, item] of this.queue) {
        if (item.topic === topic && !item.disco_done) {
          itemsToRemove.push(key);
        }
      }
      
      itemsToRemove.forEach(key => {
        this.queue.delete(key);
      });
      
      this.notifyListeners();
      await this.saveToDatabase();
      
      return itemsToRemove.length;
    }
  }

  /**
   * Infer topic from section ID
   * @param {string} sectionId - The section ID
   * @returns {string} The inferred topic
   */
  inferTopicFromSectionId(sectionId) {
    if (!sectionId) return 'unknown';
    
    const sectionIdLower = sectionId.toLowerCase();
    
    // Map section IDs to topics based on common patterns
    if (sectionIdLower.includes('skill') || sectionIdLower.includes('tech')) {
      return 'skills';
    } else if (sectionIdLower.includes('location') || sectionIdLower.includes('geo')) {
      return 'location';
    } else if (sectionIdLower.includes('experience') || sectionIdLower.includes('workexp')) {
      return 'experience';
    } else if (sectionIdLower.includes('company') || sectionIdLower.includes('stage')) {
      return 'company_stage';
    } else if (sectionIdLower.includes('title') || sectionIdLower.includes('role')) {
      return 'title';
    } else if (sectionIdLower.includes('education') || sectionIdLower.includes('degree')) {
      return 'education';
    } else if (sectionIdLower.includes('business') || sectionIdLower.includes('model')) {
      return 'business_model';
    }
    
    return 'unknown';
  }

  /**
   * Add or update item (alias for addQueueItem for backward compatibility)
   * @param {Object} queueItem - Queue item to add or update
   * @param {boolean} replace - Whether to replace existing item (ignored, always replaces)
   * @returns {Object} The added/updated item
   */
  async addOrUpdateItem(queueItem) {
    // Generate queue_id if not provided
    if (!queueItem.queue_id) {
      queueItem.queue_id = `${queueItem.topic}_${queueItem.section_id || Date.now()}`;
    }
    
    return await this.addQueueItem(queueItem);
  }

  /**
   * Get queue statistics
   * @returns {Object} Queue statistics
   */
  getStats() {
    const allItems = this.getAllItems();
    const completedItems = this.getCompletedItems();
    const pendingItems = this.getPendingItems();
    
    const topicStats = {};
    allItems.forEach(item => {
      if (!topicStats[item.topic]) {
        topicStats[item.topic] = { total: 0, completed: 0, pending: 0 };
      }
      topicStats[item.topic].total++;
      if (item.status === DISCOVERY_STATUS.DONE) {
        topicStats[item.topic].completed++;
      } else {
        topicStats[item.topic].pending++;
      }
    });

    return {
      total: allItems.length,
      completed: completedItems.length,
      pending: pendingItems.length,
      progress_percentage: this.getProgressPercentage(),
      by_topic: topicStats,
      last_saved: this.lastSaved
    };
  }

  /**
   * Bulk-add ICP requirements into the central discovery queue as pending items
   * - Creates/updates flat queue items for each requirement
   * - Marks disco_done = false (i.e., pending) and status = PENDING
   * - Applies default discovery template config per inferred topic
   * - Avoids duplicates by section_id when an unfinished item already exists
   * @param {Array} icpRequirements - Array of { section_id, section_name, bool_str, section_type }
   * @param {string} source - Optional source tag ('jd' | 'prompt' | etc.) for logging
   * @returns {Promise<Array>} Added/updated items
   */
  async addItemsFromICP(icpRequirements = [], source = 'jd') {
    if (!Array.isArray(icpRequirements) || icpRequirements.length === 0) {
      console.log('CENTRAL QUEUE: addItemsFromICP called with empty requirements');
      return [];
    }

    console.log(`CENTRAL QUEUE: addItemsFromICP(${icpRequirements.length}) from source=${source}`);
    // Prevent concurrent JD seeding from creating duplicates
    if (source === 'jd') {
      if (this.seedingLocks['jd']) {
        console.log('⏳ CENTRAL QUEUE: JD seeding already in progress - skipping duplicate add');
        return [];
      }
      this.seedingLocks['jd'] = true;
    }

    const addedOrUpdated = [];
    // Build a live index by section_id to avoid duplicates even within a single call
    const existingBySection = new Map(this.getAllItems().map(it => [it.section_id, it]));

    for (let i = 0; i < icpRequirements.length; i++) {
      const req = icpRequirements[i] || {};
      const sectionId = req.section_id || `unknown_${i}`;
      const topic = this.inferTopicFromSectionId(sectionId);

      // Check for an existing item with same section_id (update it regardless of status to avoid duplicates)
      const existing = existingBySection.get(sectionId);

      // Prepare discovery config (template) and flatten into top-level fields for UI
      const discoveryCfg = this.getDefaultDiscoveryConfig(topic) || {};
      // Only mark as completed if there is meaningful content:
      // - Non-empty bool_str OR
      // - Preselected items in template props
      const hasBool = !!(req.bool_str && req.bool_str.trim() !== '' && req.bool_str !== '(())');
      const hasPreselected = Array.isArray(discoveryCfg?.template_props?.selectedItems) && discoveryCfg.template_props.selectedItems.length > 0;
      const shouldComplete = hasBool || hasPreselected;

      const queueItemBase = {
        topic,
        section_id: sectionId,
        section_name: req.section_name || sectionId,
        bool_str: req.bool_str || '',
        section_type: req.section_type || 'Must Have',
        // Discovery state
        disco_done: shouldComplete,
        skipped: false,
        status: shouldComplete ? DISCOVERY_STATUS.DONE : DISCOVERY_STATUS.PENDING,
        // Flattened template fields for renderer
        template_type: discoveryCfg.template_type,
        ai_intro_message: discoveryCfg.ai_intro_message,
        ai_confirmation_message: discoveryCfg.ai_confirmation_message,
        ux_config: discoveryCfg.ux_config || {},
        template_props: discoveryCfg.template_props || {}
      };

      if (shouldComplete) {
        queueItemBase._forceDiscovered = true;
        queueItemBase._seededFrom = source;
      }

      if (existing) {
        // Update existing, replacing bool_str if provided
        const updated = await this.addOrUpdateItem({
          ...existing,
          ...queueItemBase,
          queue_id: existing.queue_id
        });
        addedOrUpdated.push(updated);
        existingBySection.set(sectionId, updated);
      } else {
        // New queue item with unique queue_id
        const queueId = `${topic}_${sectionId}_${Date.now()}_${i}`;
        const added = await this.addOrUpdateItem({
          ...queueItemBase,
          queue_id: queueId,
          created_at: Date.now(),
          updated_at: Date.now()
        });
        addedOrUpdated.push(added);
        existingBySection.set(sectionId, added);
      }
    }

    // Save and notify after bulk add/update
    await this.saveToDatabase();
    this.notifyListeners();

    console.log(`CENTRAL QUEUE: addItemsFromICP completed: ${addedOrUpdated.length} items processed`);
    if (source === 'jd') {
      this.seedingLocks['jd'] = false;
    }
    return addedOrUpdated;
  }

  /**
   * Restart discovery for all items in a topic:
   * - Sets disco_done=false, status=PENDING, skipped=false for matching items
   * - Saves to DB and notifies listeners
   * @param {string} topic
   */
  async restartTopic(topic) {
    if (!topic) return 0;

    const items = this.getAllItems();
    let changed = 0;

    for (const item of items) {
      if (item.topic === topic) {
        const queueId = item.queue_id || `${item.topic}_${item.section_id || 'unknown'}`;
        const cfg = this.getDefaultDiscoveryConfig(topic) || {};
        const updated = {
          ...item,
          disco_done: false,
          status: DISCOVERY_STATUS.PENDING,
          skipped: false,
          updated_at: Date.now(),
          template_type: cfg.template_type || item.template_type,
          ai_intro_message: cfg.ai_intro_message || item.ai_intro_message,
          ai_confirmation_message: cfg.ai_confirmation_message || item.ai_confirmation_message,
          ux_config: { ...(item.ux_config || {}), ...(cfg.ux_config || {}) },
          template_props: cfg.template_props || item.template_props || {}
        };
        // Ensure no preselection when allow_empty_selection is true (e.g., titles)
        if (updated.ux_config && updated.ux_config.allow_empty_selection === true) {
          if (updated.template_props && Array.isArray(updated.template_props.selectedItems)) {
            updated.template_props.selectedItems = [];
          }
        }
        this.queue.set(queueId, updated);
        changed++;
      }
    }

    if (changed > 0) {
      await this.saveToDatabase();
      this.notifyListeners();
      console.log(`🔄 CENTRAL QUEUE: Restarted discovery for topic '${topic}' - ${changed} item(s) set to pending`);
    } else {
      console.log(`ℹ️ CENTRAL QUEUE: restartTopic('${topic}') found no matching items`);
    }

    return changed;
  }

  /**
   * Restart discovery for a specific section:
   * - Sets disco_done=false, status=PENDING, skipped=false for items with section_id
   * - Saves to DB and notifies listeners
   * @param {string} sectionId
   */
  async restartSection(sectionId) {
    if (!sectionId) return 0;

    const items = this.getAllItems();
    let changed = 0;

    for (const item of items) {
      if (item.section_id === sectionId) {
        const queueId = item.queue_id || `${item.topic}_${item.section_id || 'unknown'}`;
        const updated = {
          ...item,
          disco_done: false,
          status: DISCOVERY_STATUS.PENDING,
          skipped: false,
          updated_at: Date.now()
        };
        this.queue.set(queueId, updated);
        changed++;
      }
    }

    if (changed > 0) {
      await this.saveToDatabase();
      this.notifyListeners();
      console.log(`🔄 CENTRAL QUEUE: Restarted discovery for section '${sectionId}' - ${changed} item(s) set to pending`);
    } else {
      console.log(`ℹ️ CENTRAL QUEUE: restartSection('${sectionId}') found no matching items`);
    }

    return changed;
  }

  /**
   * Fully reset queue for a session: clears backend and local cache.
   * Persists an empty items list for the session, then clears local state and notifies listeners.
   * Safe to call even if no sessionId; will clear local only in that case.
   * @param {string} sessionId - The session to reset (defaults to current)
   * @returns {Promise<boolean>}
   */
  async resetForSession(sessionId = this.sessionId) {
    const id = sessionId;
    try {
      if (!id) {
        console.warn('CENTRAL QUEUE: resetForSession called with no sessionId - clearing local only');
      } else {
        const { updateDiscoveryProgress } = await import('./api');
        console.log('CENTRAL QUEUE: Resetting discovery progress in backend for session:', id);
        try {
          await updateDiscoveryProgress(id, []); // Persist empty queue for this session
          console.log('CENTRAL QUEUE: Backend reset OK for session:', id);
        } catch (e) {
          console.error('CENTRAL QUEUE: Backend reset failed (will still clear local):', e);
        }
      }
    } catch (err) {
      console.error('CENTRAL QUEUE: resetForSession unexpected error:', err);
    } finally {
      // Clear local state regardless
      try {
        this.queue.clear();
      } catch (_) {}
      this.lastSaved = null;
      if (id) this.sessionId = id;
      // Notify listeners so UIs drop any read-only renders
      try {
        this.notifyListeners();
      } catch (_) {}
    }
    return true;
  }
}

 // Create a singleton instance
const centralDiscoveryQueue = new CentralDiscoveryQueue();

export default centralDiscoveryQueue;

// Export helper functions for easier usage
export const {
  initialize,
  addQueueItem,
  addICPRequirement,
  addItemsFromICP,
  restartTopic,
  restartSection,
  markAsDiscovered,
  getAllItems,
  getItemsByStatus,
  getPendingItems,
  getCompletedItems,
  getNextPendingItem,
  getItemByTopic,
  isFullyDiscovered,
  getProgressPercentage,
  getCurrentICPRequirements,
  addListener,
  removeListener,
  resetForSession,
  getStats
} = centralDiscoveryQueue;
